import { useEffect, useState, useRef } from 'react'
import { Gem, ChevronRight, ChevronLeft, X } from 'lucide-react'
import type { TourStep } from './tours'

interface TourOverlayProps {
  step: TourStep
  stepIndex: number
  totalSteps: number
  onNext: () => void
  onPrev: () => void
  onSkip: () => void
}

export function TourOverlay({ step, stepIndex, totalSteps, onNext, onPrev, onSkip }: TourOverlayProps) {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number; placement: string }>({ top: 0, left: 0, placement: 'bottom' })
  const tooltipRef = useRef<HTMLDivElement>(null)
  const isFirst = stepIndex === 0
  const isLast = stepIndex === totalSteps - 1
  const isWelcome = !step.target // No target = centered welcome/completion card

  // Find and track the target element
  useEffect(() => {
    if (!step.target) { setTargetRect(null); return }

    const findTarget = () => {
      const el = document.querySelector(step.target!)
      if (el) {
        const rect = el.getBoundingClientRect()
        setTargetRect(rect)

        // Scroll into view if off-screen
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          // Re-measure after scroll
          setTimeout(() => setTargetRect(el.getBoundingClientRect()), 400)
        }
      } else {
        setTargetRect(null)
      }
    }

    findTarget()
    // Re-measure on resize
    window.addEventListener('resize', findTarget)
    return () => window.removeEventListener('resize', findTarget)
  }, [step.target])

  // Position the tooltip relative to the target
  useEffect(() => {
    if (!targetRect || !tooltipRef.current) return

    const tooltip = tooltipRef.current
    const tw = tooltip.offsetWidth
    const th = tooltip.offsetHeight
    const pad = 16
    const arrowGap = 12

    // Try preferred placement, fall back if off-screen
    const placements = [step.placement || 'bottom', 'bottom', 'top', 'right', 'left']
    for (const placement of placements) {
      let top = 0, left = 0

      switch (placement) {
        case 'bottom':
          top = targetRect.bottom + arrowGap
          left = targetRect.left + targetRect.width / 2 - tw / 2
          break
        case 'top':
          top = targetRect.top - th - arrowGap
          left = targetRect.left + targetRect.width / 2 - tw / 2
          break
        case 'right':
          top = targetRect.top + targetRect.height / 2 - th / 2
          left = targetRect.right + arrowGap
          break
        case 'left':
          top = targetRect.top + targetRect.height / 2 - th / 2
          left = targetRect.left - tw - arrowGap
          break
      }

      // Clamp to viewport
      left = Math.max(pad, Math.min(left, window.innerWidth - tw - pad))
      top = Math.max(pad, Math.min(top, window.innerHeight - th - pad))

      // Check if it fits reasonably
      if (top >= pad && left >= pad && top + th <= window.innerHeight - pad) {
        setTooltipPos({ top, left, placement })
        return
      }
    }

    // Fallback: center
    setTooltipPos({
      top: window.innerHeight / 2 - 100,
      left: window.innerWidth / 2 - tw / 2,
      placement: 'center'
    })
  }, [targetRect, step.placement])

  return (
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-label="Guided tour">
      {/* Dimmed backdrop with cutout for target */}
      {isWelcome ? (
        // Full overlay for welcome/completion cards
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onSkip} />
      ) : (
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
          <defs>
            <mask id="tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 6}
                  y={targetRect.top - 6}
                  width={targetRect.width + 12}
                  height={targetRect.height + 12}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#tour-mask)" />
        </svg>
      )}

      {/* Highlight ring around target */}
      {targetRect && !isWelcome && (
        <div
          className="absolute border-2 border-amber-400 rounded-lg shadow-[0_0_0_4px_rgba(217,164,47,0.2)] pointer-events-none animate-pulse"
          style={{
            zIndex: 2,
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className={`absolute ${isWelcome ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' : ''}`}
        style={isWelcome ? {} : { top: tooltipPos.top, left: tooltipPos.left, zIndex: 3 }}
      >
        <div className={`bg-white rounded-xl shadow-2xl border border-amber-200/60 ${isWelcome ? 'w-[420px] max-w-[90vw]' : 'w-[360px] max-w-[85vw]'} overflow-hidden`}>
          {/* Gold accent bar */}
          <div className="h-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500" />

          <div className="p-5">
            {/* Header with icon */}
            <div className="flex items-start gap-3 mb-3">
              {step.icon ? (
                <span className="text-2xl flex-shrink-0 mt-0.5">{step.icon}</span>
              ) : (
                <Gem className="h-5 w-5 text-amber-500 flex-shrink-0 mt-1" />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 leading-snug">
                  {step.title}
                </h3>
              </div>
              <button
                onClick={onSkip}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 -mr-1 -mt-1"
                aria-label="Close tour"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body text */}
            <p className="text-sm text-gray-600 leading-relaxed mb-4 ml-8">
              {step.description}
            </p>

            {/* Tip callout (optional) */}
            {step.tip && (
              <div className="bg-amber-50 border border-amber-200/60 rounded-lg px-3 py-2 mb-4 ml-8">
                <p className="text-xs text-amber-800 leading-relaxed">
                  <span className="font-semibold">Tip:</span> {step.tip}
                </p>
              </div>
            )}

            {/* Footer: progress + nav */}
            <div className="flex items-center justify-between ml-8">
              {/* Progress dots */}
              <div className="flex items-center gap-1.5">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === stepIndex
                        ? 'w-4 bg-amber-500'
                        : i < stepIndex
                        ? 'w-1.5 bg-amber-300'
                        : 'w-1.5 bg-gray-200'
                    }`}
                  />
                ))}
                <span className="text-[10px] text-gray-400 ml-1.5">
                  {stepIndex + 1}/{totalSteps}
                </span>
              </div>

              {/* Navigation buttons */}
              <div className="flex items-center gap-2">
                {!isFirst && (
                  <button
                    onClick={onPrev}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors px-2 py-1.5 rounded-md hover:bg-gray-100"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Back
                  </button>
                )}
                <button
                  onClick={onNext}
                  className="flex items-center gap-1 text-xs font-medium text-white bg-amber-500 hover:bg-amber-600 transition-colors px-3 py-1.5 rounded-md shadow-sm"
                >
                  {isLast ? 'Finish' : 'Next'}
                  {!isLast && <ChevronRight className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
